import { z } from "zod";

/**
 * 40-character lowercase hex git sha. We never accept the short form at
 * the ref layer — a ref is a precise pointer, not a navigation aid.
 * (The deployment trigger schema separately allows short shas for
 * developer ergonomics; refs are stricter because the receive-hook
 * source-of-truth is always the full sha.)
 */
export const VibeRefShaSchema = z
  .string()
  .trim()
  .length(40, "sha must be exactly 40 characters")
  .regex(/^[0-9a-f]+$/, "sha must be lowercase hexadecimal");

/**
 * The all-zeros sha is the git protocol marker for "no commit yet" —
 * it appears as oldSha on the first push to a brand-new ref, and as
 * newSha when a ref is being deleted. We accept it in advance inputs
 * but never persist it.
 */
export const VIBE_ZERO_SHA = "0000000000000000000000000000000000000000";

export const VibeRefAdvanceShaSchema = z
  .string()
  .trim()
  .length(40, "sha must be exactly 40 characters")
  .regex(/^[0-9a-f]+$/, "sha must be lowercase hexadecimal");

/**
 * Fully-qualified ref name. "refs/heads/<branch>" or "refs/tags/<tag>".
 * The qualifier prefix is required so the row is unambiguous in the DB.
 * Disallows path-component shapes that git itself forbids (leading dot,
 * trailing slash, double-dot, `@{`, etc.) at a coarse level — Forgejo
 * enforces the full spec on the way in.
 */
export const VibeRefNameSchema = z
  .string()
  .trim()
  .min(1, "refName is required")
  .max(255, "refName must be ≤ 255 characters")
  .regex(
    /^refs\/(heads|tags)\/[A-Za-z0-9._\-/]+$/,
    "refName must start with refs/heads/ or refs/tags/ and contain only safe characters"
  )
  .refine((s) => !s.includes(".."), { message: "refName must not contain '..'" })
  .refine((s) => !s.endsWith("/"), { message: "refName must not end with '/'" })
  .refine((s) => !s.endsWith(".lock"), { message: "refName must not end with '.lock'" });

export const VibeRefSchema = z.object({
  id: z.string().uuid(),
  vibeGitProjectId: z.string().uuid(),
  organizationId: z.string(),
  refName: VibeRefNameSchema,
  sha: VibeRefShaSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeRefDto = z.infer<typeof VibeRefSchema>;

// ----------------------------------------------------------------------------
// List refs
// ----------------------------------------------------------------------------

export const ListVibeRefsPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const ListVibeRefsResponseSchema = z.object({
  refs: z.array(VibeRefSchema)
});

// ----------------------------------------------------------------------------
// Get ref
// ----------------------------------------------------------------------------

export const GetVibeRefPathVarsSchema = z.object({
  appId: z.string().uuid()
});

/**
 * Query-string lookup avoids the "ref names contain slashes" wildcard-
 * route problem (`refs/heads/main` would otherwise need an awkward
 * `@Get(":refName(.*)")` route definition). A single `name` query param
 * is unambiguous, encodable, and matches how git tooling typically
 * exposes ref lookup APIs.
 */
export const GetVibeRefQuerySchema = z.object({
  name: VibeRefNameSchema
});

export const GetVibeRefResponseSchema = z.object({
  ref: VibeRefSchema
});
